import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Site } from "@/bryntum-lib/models/Site";
import { sitesApi, sitesApiByBoardId } from "@/network/network.jsx";
import {
  type DomainStatusResponse,
  mergeDomainResponseIntoSite,
  normalizeSetupInstructions,
  parseDomainApiError,
  type NormalizedDomainSetupInstructions,
} from "../types/domain";

const DOMAIN_POLL_MS = 8_000;

function domainApi(boardSlug: string | undefined, boardId: number | undefined) {
  if (boardSlug) return sitesApi(boardSlug);
  if (boardId != null) return sitesApiByBoardId(boardId);
  return null;
}

interface UseCustomDomainOptions {
  site: Site | null;
  boardSlug: string | undefined;
  boardId: number | undefined;
  isReadOnly: boolean;
  /** Called after domain fields merge into the site model (for parent re-render). Must not reset unrelated form state. */
  onDomainChanged?: () => void;
}

export function useCustomDomain({
  site,
  boardSlug,
  boardId,
  isReadOnly,
  onDomainChanged,
}: UseCustomDomainOptions) {
  const [domainInput, setDomainInput] = useState(site?.domain ?? "");
  const [instructions, setInstructions] = useState<NormalizedDomainSetupInstructions | null>(null);
  const [lastError, setLastError] = useState("");
  const [isBusy, setIsBusy] = useState(false);
  const statusFetchSiteIdRef = useRef<number | null>(null);

  const status = site?.domainStatus ?? "unset";
  const api = useMemo(() => domainApi(boardSlug, boardId), [boardSlug, boardId]);

  const applyResponse = useCallback(
    (data: DomainStatusResponse) => {
      if (!site) return;
      mergeDomainResponseIntoSite(site as unknown as Parameters<typeof mergeDomainResponseIntoSite>[0], data);
      setInstructions(normalizeSetupInstructions(data.setup_instructions));
      setLastError(data.domain_last_error ?? "");
      setDomainInput(data.domain ?? "");
      onDomainChanged?.();
    },
    [site, onDomainChanged],
  );

  const refreshStatus = useCallback(async () => {
    if (!api) return;
    try {
      const res = await api.getDomainStatus({
        redirect_uri: window.location.href.split("?")[0],
      });
      applyResponse(res.data);
    } catch {
      // ignore background refresh errors
    }
  }, [api, applyResponse]);

  useEffect(() => {
    if (!site || !api) return;
    setDomainInput(site.domain ?? "");
  }, [site?.id, api, site]);

  useEffect(() => {
    if (!site?.id || !api) return;
    if (statusFetchSiteIdRef.current === site.id) return;
    statusFetchSiteIdRef.current = site.id;
    void refreshStatus();
  }, [site?.id, api, refreshStatus]);

  useEffect(() => {
    if (status !== "pending_verification" && status !== "provisioning") return undefined;
    const id = setInterval(() => void refreshStatus(), DOMAIN_POLL_MS);
    return () => clearInterval(id);
  }, [status, refreshStatus]);

  const handleSave = useCallback(async () => {
    if (!api || isReadOnly) return false;
    const domain = domainInput.trim();
    if (!domain) {
      toast.error("Enter a domain name.");
      return false;
    }
    setIsBusy(true);
    try {
      const res = await api.saveDomain({ domain });
      applyResponse(res.data);
      toast.success("Domain saved — add DNS records to finish setup.");
      return true;
    } catch (error) {
      toast.error(parseDomainApiError(error, "Could not save domain."));
      return false;
    } finally {
      setIsBusy(false);
    }
  }, [api, isReadOnly, domainInput, applyResponse]);

  const handleConfirm = useCallback(async () => {
    if (!api || isReadOnly) return false;
    setIsBusy(true);
    try {
      const res = await api.confirmDomain();
      applyResponse(res.data);
      if (res.data?.confirmed) {
        toast.success("DNS verified — provisioning your domain.");
      } else {
        toast.info("DNS records not detected yet. Try again in a few minutes.");
      }
      return true;
    } catch (error) {
      toast.error(parseDomainApiError(error, "Could not verify DNS records."));
      return false;
    } finally {
      setIsBusy(false);
    }
  }, [api, isReadOnly, applyResponse]);

  const handleRemove = useCallback(async () => {
    if (!api || isReadOnly) return false;
    const confirmMessage =
      status === "active"
        ? "Remove custom domain? Your site will revert to the default subdomain URL."
        : "Cancel domain setup? This removes the custom domain from this site.";
    if (!window.confirm(confirmMessage)) {
      return false;
    }
    setIsBusy(true);
    try {
      const res = await api.removeDomain();
      applyResponse(res.data);
      setInstructions(null);
      toast.success(status === "active" ? "Custom domain removed." : "Domain setup cancelled.");
      return true;
    } catch (error) {
      toast.error(parseDomainApiError(error, "Could not remove domain."));
      return false;
    } finally {
      setIsBusy(false);
    }
  }, [api, isReadOnly, applyResponse, status]);

  const handleChangeDomain = useCallback(async () => {
    if (!api || isReadOnly) return false;
    if (
      !window.confirm(
        "Change domain? This clears the current DNS setup so you can enter a different hostname.",
      )
    ) {
      return false;
    }
    setIsBusy(true);
    try {
      const res = await api.removeDomain();
      applyResponse(res.data);
      setInstructions(null);
      setDomainInput("");
      toast.success("Enter a new domain to continue setup.");
      return true;
    } catch (error) {
      toast.error(parseDomainApiError(error, "Could not change domain."));
      return false;
    } finally {
      setIsBusy(false);
    }
  }, [api, isReadOnly, applyResponse]);

  return {
    status,
    domainInput,
    setDomainInput,
    instructions,
    lastError,
    isBusy,
    refreshStatus,
    handleSave,
    handleConfirm,
    handleRemove,
    handleChangeDomain,
    hasDomainApi: Boolean(api),
  };
}
